import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
} from 'react'
import PropTypes from 'prop-types'
import { useSearchParams } from 'react-router-dom'
import { useConfig } from './ConfigContext'
import { useData } from './DataContext'

const LocationContext = createContext()

const initialState = {
  selectedDistrict: null,
  selectedSubcounty: null,
  selectedParish: null,
  selectedVillage: null,
  currentLocationCode: null,
  showAllDistricts: false, // For multi-district mode
}

function locationReducer(state, action) {
  switch (action.type) {
    case 'SET_DISTRICT':
      return {
        selectedDistrict: action.districtId,
        selectedSubcounty: null,
        selectedParish: null,
        selectedVillage: null,
        showAllDistricts: action.districtId === null, // "All Districts" when null
        currentLocationCode: action.locationCode,
      }

    case 'SET_SUBCOUNTY':
      return {
        ...state,
        selectedSubcounty: action.subcountyId,
        selectedParish: null,
        selectedVillage: null,
        showAllDistricts: false, // Clear "All Districts" when selecting subcounty
        currentLocationCode: action.locationCode,
      }

    case 'SET_PARISH':
      return {
        ...state,
        selectedParish: action.parishId,
        selectedVillage: null,
        showAllDistricts: false, // Clear "All Districts" when selecting parish
        currentLocationCode: action.locationCode,
      }

    case 'SET_VILLAGE':
      return {
        ...state,
        selectedVillage: action.villageId,
        showAllDistricts: false, // Clear "All Districts" when selecting village
        currentLocationCode: action.locationCode,
      }

    case 'RESET':
      return {
        ...initialState,
        showAllDistricts: action.toAllDistricts || false,
      }

    case 'SET_ALL_DISTRICTS':
      return {
        ...initialState,
        showAllDistricts: true,
        selectedDistrict: null,
      }

    default:
      return state
  }
}

export function LocationProvider({ children }) {
  const [state, dispatch] = useReducer(locationReducer, initialState)
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    config,
    isMultiDistrictMode,
    isSingleDistrictMode,
    getFocusedDistrict,
    getDefaultLocationSelection,
  } = useConfig()
  const { locations } = useData()

  // Helper to find location info by ID
  const findLocationById = useCallback(
    (locationId, level = null) => {
      if (!locations?.districts || !locationId) return null

      // Parse level from ID prefix if not provided
      const actualLevel = level || locationId.split('-')[0]

      if (actualLevel === 'd') {
        return locations.districts.find(d => d.id === locationId)
      }

      // For subcounty, parish, village - search nested structure
      for (const district of locations.districts) {
        if (actualLevel === 's') {
          const subcounty = district.subcounties?.find(s => s.id === locationId)
          if (subcounty) return subcounty
        } else {
          for (const subcounty of district.subcounties || []) {
            if (actualLevel === 'p') {
              const parish = subcounty.parishes?.find(p => p.id === locationId)
              if (parish) return parish
            } else if (actualLevel === 'v') {
              for (const parish of subcounty.parishes || []) {
                const village = parish.villages?.find(v => v.id === locationId)
                if (village) return village
              }
            }
          }
        }
      }
      return null
    },
    [locations]
  )

  // Helper to get location code from ID
  const getLocationCodeById = useCallback(
    locationId => {
      if (!locationId || !locations) return null
      const location = findLocationById(locationId)
      return location?.code || null
    },
    [findLocationById, locations]
  )

  // Initialize from URL params and config on mount
  useEffect(() => {
    if (!locations) return // Wait for locations to load
    const district = searchParams.get('district')
    const subcounty = searchParams.get('subcounty')
    const parish = searchParams.get('parish')
    const village = searchParams.get('village')

    // Handle special "all" value for multi-district mode
    if (district === 'all' && isMultiDistrictMode()) {
      dispatch({ type: 'SET_ALL_DISTRICTS' })
      return
    }

    // Initialize based on URL params or config defaults
    const defaultSelection = getDefaultLocationSelection()

    if (district) {
      const locationCode = getLocationCodeById(district)
      dispatch({
        type: 'SET_DISTRICT',
        districtId: district,
        locationCode,
      })
    } else if (defaultSelection.district) {
      const locationCode = getLocationCodeById(defaultSelection.district)
      dispatch({
        type: 'SET_DISTRICT',
        districtId: defaultSelection.district,
        locationCode,
      })
    } else if (isMultiDistrictMode()) {
      dispatch({ type: 'SET_ALL_DISTRICTS' })
    }

    if (subcounty) {
      const locationCode = getLocationCodeById(subcounty)
      dispatch({ type: 'SET_SUBCOUNTY', subcountyId: subcounty, locationCode })
    }
    if (parish) {
      const locationCode = getLocationCodeById(parish)
      dispatch({ type: 'SET_PARISH', parishId: parish, locationCode })
    }
    if (village) {
      const locationCode = getLocationCodeById(village)
      dispatch({ type: 'SET_VILLAGE', villageId: village, locationCode })
    }
  }, [
    config,
    locations,
    getLocationCodeById,
    getDefaultLocationSelection,
    isMultiDistrictMode,
    searchParams,
  ]) // Re-run when dependencies change

  const updateURLParams = useCallback(
    newParams => {
      const current = Object.fromEntries(searchParams.entries())

      // Remove location-related params first
      delete current.district
      delete current.subcounty
      delete current.parish
      delete current.village

      // Add new location params
      const updatedParams = { ...current, ...newParams }

      // Remove undefined/null values
      Object.keys(updatedParams).forEach(key => {
        if (updatedParams[key] === null || updatedParams[key] === undefined) {
          delete updatedParams[key]
        }
      })

      setSearchParams(updatedParams)
    },
    [searchParams, setSearchParams]
  )

  const setDistrict = useCallback(
    districtId => {
      const locationCode = getLocationCodeById(districtId)
      dispatch({ type: 'SET_DISTRICT', districtId, locationCode })
      updateURLParams({ district: districtId })
    },
    [updateURLParams, getLocationCodeById]
  )

  const setSubcounty = useCallback(
    subcountyId => {
      const locationCode = getLocationCodeById(subcountyId)
      dispatch({ type: 'SET_SUBCOUNTY', subcountyId, locationCode })
      updateURLParams({
        district: state.selectedDistrict,
        subcounty: subcountyId,
      })
    },
    [updateURLParams, state.selectedDistrict, getLocationCodeById]
  )

  const setParish = useCallback(
    parishId => {
      const locationCode = getLocationCodeById(parishId)
      dispatch({ type: 'SET_PARISH', parishId, locationCode })
      updateURLParams({
        district: state.selectedDistrict,
        subcounty: state.selectedSubcounty,
        parish: parishId,
      })
    },
    [
      updateURLParams,
      state.selectedDistrict,
      state.selectedSubcounty,
      getLocationCodeById,
    ]
  )

  const setVillage = useCallback(
    villageId => {
      const locationCode = getLocationCodeById(villageId)
      dispatch({ type: 'SET_VILLAGE', villageId, locationCode })
      updateURLParams({
        district: state.selectedDistrict,
        subcounty: state.selectedSubcounty,
        parish: state.selectedParish,
        village: villageId,
      })
    },
    [
      updateURLParams,
      state.selectedDistrict,
      state.selectedSubcounty,
      state.selectedParish,
      getLocationCodeById,
    ]
  )

  const reset = useCallback(() => {
    const toAllDistricts = isMultiDistrictMode()
    dispatch({ type: 'RESET', toAllDistricts })
    if (toAllDistricts) {
      updateURLParams({ district: 'all' })
    } else {
      updateURLParams({})
    }
  }, [updateURLParams, isMultiDistrictMode])

  const setAllDistricts = useCallback(() => {
    dispatch({ type: 'SET_ALL_DISTRICTS' })
    updateURLParams({ district: 'all' })
  }, [updateURLParams])

  // Helper functions for multi-district functionality
  const isViewingAllDistricts = useCallback(() => {
    return state.showAllDistricts && isMultiDistrictMode()
  }, [state.showAllDistricts, isMultiDistrictMode])

  const getLocationDisplayMode = useCallback(() => {
    if (isSingleDistrictMode()) {
      return 'single-district'
    }
    return state.showAllDistricts ? 'all-districts' : 'selected-district'
  }, [isSingleDistrictMode, state.showAllDistricts])

  const shouldShowDistrictSelector = useCallback(() => {
    return isMultiDistrictMode()
  }, [isMultiDistrictMode])

  const value = {
    ...state,
    setDistrict,
    setSubcounty,
    setParish,
    setVillage,
    reset,
    setAllDistricts,
    isViewingAllDistricts,
    getLocationDisplayMode,
    shouldShowDistrictSelector,
    // Helper functions
    findLocationById,
    getLocationCodeById,
    // Expose config-related helpers
    isMultiDistrictMode: isMultiDistrictMode(),
    isSingleDistrictMode: isSingleDistrictMode(),
    focusedDistrict: getFocusedDistrict(),
  }

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  )
}

export function useLocation() {
  const context = useContext(LocationContext)
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider')
  }
  return context
}

LocationProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
